import styled, { css, keyframes } from "styled-components";
import { motion } from "framer-motion";

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const pageShellStyles = css`
  width: 100%;
  min-height: 100vh;
  color: #fff;
  position: relative;
  overflow: hidden;
  display: block;
  align-self: stretch;
  padding: clamp(72px, 8vh, 112px) 0 clamp(84px, 10vh, 132px);
`;

export const pageInnerStyles = css`
  width: min(calc(100% - (var(--content-gutter) * 2)), var(--content-wide-width));
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const stackedSectionsStyles = css`
  display: flex;
  flex-direction: column;
  gap: var(--section-gap);
`;

export const sectionIntroStyles = css`
  max-width: min(48rem, 100%);
  display: grid;
  gap: 14px;
  margin-bottom: clamp(28px, 3vw, 38px);
  text-align: left;
  justify-items: start;
`;

export const heroIntroStyles = css`
  max-width: min(60rem, 100%);
  display: grid;
  gap: clamp(18px, 2vw, 26px);
`;

export const heroIntroCenteredStyles = css`
  ${heroIntroStyles}
  margin: 0 auto;
  text-align: center;
  justify-items: center;
`;

export const heroBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
`;

export const heroTitleStyles = css`
  margin: 0;
  font-size: clamp(44px, 7vw, 76px);
  line-height: 0.96;
  letter-spacing: -0.05em;
  font-weight: 800;
`;

export const heroLeadStyles = css`
  margin: 0;
  max-width: min(54rem, 100%);
  font-size: clamp(18px, 2.5vw, 22px);
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.82);
`;

export const sectionKickerStyles = css`
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

export const sectionHeadingStyles = css`
  margin: 0;
  font-size: clamp(36px, 4.7vw, 60px);
  line-height: 1;
  letter-spacing: -0.04em;
  font-weight: 800;
`;

export const sectionLeadStyles = css`
  margin: 0;
  max-width: min(46rem, 100%);
  font-size: 18px;
  line-height: 1.74;
  color: rgba(255, 255, 255, 0.78);
`;

export const sectionLinkStyles = css`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.92);
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateX(2px);
    opacity: 0.9;
  }
`;

export const glassPanelStyles = css`
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02)),
    rgba(8, 8, 8, 0.52);
  backdrop-filter: blur(14px);
`;

export const primaryButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  min-height: 58px;
  padding: 0 28px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(231, 231, 231, 0.94));
  color: #050505;
  text-align: center;
  text-decoration: none;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.82),
    0 16px 40px rgba(0, 0, 0, 0.24);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.92),
      0 22px 46px rgba(0, 0, 0, 0.28);
    border-color: rgba(255, 255, 255, 0.34);
  }

  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
    transform: none;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.74),
      0 10px 26px rgba(0, 0, 0, 0.18);
  }
`;

export const secondaryButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  min-height: 56px;
  padding: 0 28px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  text-align: center;
  text-decoration: none;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

export const ContentPageShell = styled.div.attrs({ "data-contrast-shelter": "" })`
  ${pageShellStyles}
`;

export const ContentPageInner = styled.div`
  ${pageInnerStyles}
  ${stackedSectionsStyles}
`;

export const AnimatedSection = styled(motion.section).attrs((p) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: p.$amount || 0.2 },
  transition: {
    duration: 0.56,
    delay: (p.$i || 0) * 0.04,
    ease: [0.22, 0.61, 0.36, 1],
  },
  "data-track-section": "true",
  "data-section-id":
    p["data-section-id"] ||
    (Number.isFinite(p.$i) ? `section-${Number(p.$i) + 1}` : undefined),
  ...(p.$shelter === false ? {} : { "data-contrast-shelter": "" }),
}))`
  width: 100%;
`;

export const HeroIntro = styled.div`
  ${heroIntroStyles}
`;

export const CenteredHeroIntro = styled.div`
  ${heroIntroCenteredStyles}
`;

export const HeroBadge = styled.div`
  ${heroBadgeStyles}
`;

export const HeroTitle = styled.h1`
  ${heroTitleStyles}
`;

export const HeroLead = styled.p`
  ${heroLeadStyles}
`;

export const SectionIntro = styled.div`
  ${sectionIntroStyles}
`;

export const SectionKicker = styled.span`
  ${sectionKickerStyles}
`;

export const SectionHeading = styled.h2`
  ${sectionHeadingStyles}
`;

export const SectionLead = styled.p`
  ${sectionLeadStyles}
`;

export const SectionLink = styled.button`
  ${sectionLinkStyles}
`;

export const GlassPanel = styled.div`
  ${glassPanelStyles}
`;

export const PrimaryButton = styled.button`
  ${primaryButtonStyles}
`;

export const SecondaryButton = styled.button`
  ${secondaryButtonStyles}
`;
